/**
 * @file 本だなの上に置く「AI 解説」「AI 採点」への入口である。
 *
 * @what: まなぶ人の記録を操作ごとに数え、件数を付けたリンクを1行で出す。0件の操作は出さない。
 *   押すと一覧の面 (`/study/:familyId/asks?kind=`) へ移る。
 * @why: 本だなで主役にはしないが、ページを覚えていなくても読み返せる入口が必要である。操作の名前を
 *   そのまま出すのは、押したボタンとどこに残るかを対応させるためである。
 * @failure: 取得に失敗したら何も出さない (本だなの邪魔をしない。詳細は console)。
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ASK_PRESET_LABELS,
  fetchAskRecords,
  type StudyAskRecordSummary,
} from "../api/askRecords";

/** 入口に出す操作の並び (押すボタンと同じ言葉、同じ順)。 */
const OPS: readonly { preset: string; label: string }[] = [
  { preset: "ai_explain", label: ASK_PRESET_LABELS.ai_explain! },
  { preset: "ai_grade", label: ASK_PRESET_LABELS.ai_grade! },
];

export function AskRecordLinks({
  familyId,
  learnerPersonId,
}: {
  familyId: string;
  learnerPersonId: string | null;
}) {
  const [records, setRecords] = useState<StudyAskRecordSummary[] | null>(null);

  useEffect(() => {
    if (learnerPersonId === null) {
      setRecords(null);
      return;
    }
    const controller = new AbortController();
    fetchAskRecords(familyId, learnerPersonId, controller.signal)
      .then((rs) => {
        if (!controller.signal.aborted) setRecords(rs);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to count ask records", err);
        setRecords(null);
      });
    return () => controller.abort();
  }, [familyId, learnerPersonId]);

  if (records === null || records.length === 0) return null;
  const shown = OPS.map((op) => ({
    ...op,
    count: records.filter((r) => r.first_preset === op.preset).length,
  })).filter((op) => op.count > 0);
  if (shown.length === 0) return null;

  return (
    <p className="study-ask-links">
      {shown.map((op) => (
        <Link
          key={op.preset}
          className="study-ask-link"
          to={`/study/${encodeURIComponent(familyId)}/asks?kind=${op.preset}`}
        >
          {op.label}
          <span className="study-ask-link-count">{op.count}</span>
        </Link>
      ))}
    </p>
  );
}
